'use client'

import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { supabase } from '@/integrations/supabase/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { AuthLayout } from '@/components/auth/AuthLayout'
import EmailVerificationMessage from '@/components/auth/EmailVerificationMessage'
import { Eye, EyeOff, Building, ShieldCheck, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export default function FacilitySignupPage() {
    const [facilityName, setFacilityName] = useState('')
    const [managerName, setManagerName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [showEmailVerification, setShowEmailVerification] = useState(false)

    const navigate = useNavigate()
    const { t } = useTranslation()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        setError('')

        if (password.length < 6) {
            setError(t('facility.signup.passwordTooShort'))
            setIsLoading(false)
            return
        }

        try {
            const { data, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        name: managerName,
                        is_facility_admin: true,
                        facility_name: facilityName
                    },
                    emailRedirectTo: `${window.location.origin}/auth/verify`,
                },
            })

            if (signUpError) {
                if (signUpError.message.includes('already registered')) {
                    setError(t('facility.signup.alreadyExists'))
                } else {
                    throw signUpError
                }
            } else if (data?.user) {
                if (!data.user.email_confirmed_at) {
                    setShowEmailVerification(true)
                } else {
                    navigate('/facility-admin', { replace: true })
                }
            }
        } catch (err: unknown) {
            console.error('Facility Signup error:', err)
            setError((err as Error)?.message || t('facility.signup.genericError'))
        } finally {
            setIsLoading(false)
        }
    }

    if (showEmailVerification) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20 flex items-center justify-center py-12 px-4">
                <EmailVerificationMessage email={email} onResendVerification={() => { }} isResending={false} />
            </div>
        )
    }

    return (
        <AuthLayout
            title={t('facility.signup.title')}
            subtitle={t('facility.signup.subtitle')}
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                        {error}
                    </div>
                )}

                {/* Feature Highlights for B2B */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-emerald-50 p-3 rounded-lg text-center border border-emerald-100">
                        <Users className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                        <p className="text-xs font-semibold text-emerald-900">{t('facility.signup.features.perSeat')}</p>
                        <p className="text-[10px] text-emerald-700">{t('facility.signup.features.perSeatDesc')}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg text-center border border-blue-100">
                        <ShieldCheck className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                        <p className="text-xs font-semibold text-blue-900">{t('facility.signup.features.private')}</p>
                        <p className="text-[10px] text-blue-700">{t('facility.signup.features.privateDesc')}</p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg text-center border border-amber-100">
                        <Building className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                        <p className="text-xs font-semibold text-amber-900">{t('facility.signup.features.magicLinks')}</p>
                        <p className="text-[10px] text-amber-700">{t('facility.signup.features.magicLinksDesc')}</p>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('facility.signup.facilityNameLabel')}</label>
                    <Input
                        type="text"
                        value={facilityName}
                        onChange={(e) => setFacilityName(e.target.value)}
                        required
                        placeholder={t('facility.signup.facilityNamePlaceholder')}
                        className="text-lg h-14 px-4 rounded-xl border-2 border-slate-200 focus:border-emerald-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('facility.signup.managerNameLabel')}</label>
                    <Input
                        type="text"
                        value={managerName}
                        onChange={(e) => setManagerName(e.target.value)}
                        required
                        placeholder={t('facility.signup.managerNamePlaceholder')}
                        className="text-lg h-14 px-4 rounded-xl border-2 border-slate-200 focus:border-emerald-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('facility.signup.emailLabel')}</label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder={t('facility.signup.emailPlaceholder')}
                        className="text-lg h-14 px-4 rounded-xl border-2 border-slate-200 focus:border-emerald-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('facility.signup.passwordLabel')}</label>
                    <div className="relative">
                        <Input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder={t('facility.signup.passwordPlaceholder')}
                            className="text-lg h-14 pl-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-emerald-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                <Button type="submit" className="w-full h-14 text-lg rounded-xl bg-emerald-600 hover:bg-emerald-700" disabled={isLoading}>
                    {isLoading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
                    {isLoading ? t('facility.signup.submitting') : t('facility.signup.submitButton')}
                </Button>

                <p className="text-center text-sm text-slate-600 mt-4">
                    {t('facility.signup.hasAccount')}{' '}
                    <Link to="/auth/login" className="text-emerald-600 font-semibold">{t('facility.signup.signInLink')}</Link>
                </p>
            </form>
        </AuthLayout>
    )
}
